import React, {
  ComponentProps,
  ElementType,
  FocusEvent,
  InputHTMLAttributes,
  KeyboardEvent,
  MouseEvent,
  Ref,
  forwardRef,
  useRef,
} from 'react';
import clsx from 'clsx';
import TextField, {
  TextFieldProps,
} from '@mui/material/TextField';
import {
  InputBaseComponentProps,
} from '@mui/material/InputBase';
import {
  IMaskInput,
} from 'react-imask';
import {
  InputMask,
} from 'imask';

export type CursorPositionOnFocus = 'start' | 'end' | 'clickedPosition' | 'selectAll';

type Position = {
  start: number;
  end: number;
};

type MaskedInputProps = {
  mask: ComponentProps<typeof IMaskInput>['mask'] | any[] | readonly any[];
  unmask?: ComponentProps<typeof IMaskInput>['unmask'];
  onAccept?: (value: any, maskRef: InputMask<any>, e?: InputEvent) => void;
  onComplete?: (value: any, maskRef: InputMask<any>, e?: InputEvent) => void;
};

type MaskedInputElementProps = InputBaseComponentProps & MaskedInputProps & {
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
};

const MaskedInputComponent = forwardRef<HTMLInputElement, MaskedInputElementProps>(
  (props, ref) => (
    <IMaskInput
      {...props}
      inputRef={ref}
    />
  ),
);

type Props = Omit<TextFieldProps, 'value' | 'defaultValue' | 'onChange' | 'slotProps'> & MaskedInputProps & {
  value?: TextFieldProps['value'] | null;
  defaultValue?: TextFieldProps['defaultValue'] | null;
  cursorPositionOnFocus?: CursorPositionOnFocus;
  maskedPosition?: Position;
  showErrorText?: boolean;
  slotProps?: TextFieldProps['slotProps'];
  inputRef?: Ref<HTMLInputElement>;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
};

function computeSelection(
  input: HTMLInputElement,
  cursorPositionOnFocus: CursorPositionOnFocus,
  maskedPosition?: Position,
): Position {
  const {
    start: maskedStart = 0,
    end: maskedEnd = 0,
  } = maskedPosition ?? {};

  const position: Position = { start: 0, end: 0 };

  switch (cursorPositionOnFocus) {
  case 'start':
    position.start = 0;
    position.end = 0;
    break;
  case 'end':
    position.start = input.value.length;
    position.end = input.value.length;
    break;
  case 'clickedPosition':
    position.start = input.selectionStart ?? 0;
    position.end = input.selectionEnd ?? 0;
    break;
  case 'selectAll':
  default:
    position.start = maskedStart;
    position.end = input.value.length - maskedEnd;
  }

  return position;
}

export default function MaskedTextField({
  mask,
  unmask,
  onAccept,
  onComplete,
  className,
  value,
  onFocus,
  cursorPositionOnFocus = 'selectAll',
  maskedPosition,
  showErrorText: _showErrorText,
  inputRef,
  inputProps,
  slotProps,
  ...props
}: Props) {
  const handleAccept = (acceptedValue: string, maskRef: InputMask<any>) => {
    const currentValue = typeof value === 'string' || typeof value === 'number'
      ? String(value)
      : '';

    if (acceptedValue === currentValue) {
      return;
    }

    onAccept?.(acceptedValue, maskRef);
  };

  const doAfterHandlersAndBeforeNextPaint = (fn: () => void) => {
    if (typeof queueMicrotask === 'function') {
      queueMicrotask(fn);
      return;
    }

    Promise.resolve().then(fn);
  };

  const selectAppropriately = (input: HTMLInputElement) => {
    if (typeof input.setSelectionRange === 'function') {
      const {
        start,
        end,
      } = computeSelection(input, cursorPositionOnFocus, maskedPosition);

      input.setSelectionRange(
        Math.max(start, 0),
        Math.max(0, Math.min(end, input.value.length)),
      );
    }
  };

  const isFocusByPointerEventChain = useRef(false);

  const handleBlur = () => {
    isFocusByPointerEventChain.current = false;
  };

  const handlePointerDown = () => {
    isFocusByPointerEventChain.current = true;
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    const target = event.currentTarget;

    if (!isFocusByPointerEventChain.current) {
      doAfterHandlersAndBeforeNextPaint(() => selectAppropriately(target));
    }

    isFocusByPointerEventChain.current = false;
    onFocus?.(event);
  };

  const handleMouseDown = (event: MouseEvent<HTMLInputElement>) => {
    const target = event.currentTarget;

    if (event.detail >= 3) {
      event.preventDefault();
      selectAppropriately(target);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const target = event.currentTarget;

    if ((event.metaKey || event.ctrlKey) && event.key.toUpperCase() === 'A') {
      event.preventDefault();
      selectAppropriately(target);
    }

    if (event.key === 'Tab') {
      isFocusByPointerEventChain.current = false;
    }
  };

  const maskedInputProps: MaskedInputElementProps = {
    mask,
    unmask,
    value: value ?? '',
    onAccept: handleAccept,
    onComplete,
    onFocus: handleFocus,
    inputRef,
  };

  return (
    <TextField
      {...props}
      value={value ?? ''}
      className={clsx('populate-input', className)}
      slotProps={{
        ...slotProps,
        input: {
          ...slotProps?.input,
          inputComponent: MaskedInputComponent as ElementType<InputBaseComponentProps>,
          inputProps: maskedInputProps,
          inputRef,
        },
        htmlInput: {
          ...slotProps?.htmlInput,
          ...inputProps,
          spellCheck: false,
          onBlur: handleBlur,
          onKeyDown: handleKeyDown,
          onMouseDown: handleMouseDown,
          onPointerDown: handlePointerDown,
        },
      }}
    />
  );
}
